import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid2 as Grid,
  Stack,
  TextField,
  Typography,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Controller, FormProvider } from "react-hook-form";
import { Admins, SaveButton } from "@/organism";
import useEditForm from "./useEditForm";
import type {
  AdmDetailResponseDto,
  AdmPwdUpdateRequestDto,
  AdmUpdateRequestDto,
} from "@/dto/AdmDto";
import { Yn } from "@/constants/code";

/**
 * 관리자 정보 수정 폼 프로퍼티
 */
export type EditFormProps = {
  /**
   * 관리자 정보
   * @type {AdmDetailResponseDto}
   */
  admInfo: AdmDetailResponseDto;
  /**
   * 관리자 정보 업데이트 이벤트
   *
   * @type {(adm: AdmUpdateRequestDto) => void}
   */
  onAdmUpdate: (adm: AdmUpdateRequestDto, id: string) => void;

  /**
   * 관리자 비밀번호 업데이트 이벤트
   *
   * @type {(adm: AdmPwdUpdateRequestDto) => void}
   */
  onAdmPwdUpdate: (id: string, adm: AdmPwdUpdateRequestDto) => void;

  onDelete?: (id: string) => void;
};

/**
 * 관리자 정보 수정 폼
 *
 * @returns {JSX.Element}
 *
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @created 2025-01-30
 */
export const EditForm = (props: EditFormProps) => {
  const { admInfo, onAdmUpdate, onAdmPwdUpdate } = props;

  const {
    updateMethods,
    pwdUpdateMethods,
    handleAdmUpdate,
    handleAdmPwdUpdate,
  } = useEditForm({ admInfo, onAdmUpdate, onAdmPwdUpdate });

  if (!props.admInfo) return null;

  return (
    <Box display={"flex"} flexDirection={"column"} gap={4}>
      <FormProvider {...updateMethods}>
        <Stack direction={"column"} gap={4}>
          <Box>
            <Typography variant={"h5"} sx={{ mb: 2 }}>
              관리자 정보
            </Typography>
            <Grid container spacing={2} columnSpacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <FormLabel required>관리자명</FormLabel>
                  <TextField
                    fullWidth
                    type="text"
                    variant="outlined"
                    color="primary"
                    size="small"
                    placeholder="관리자 이름을 입력해주세요."
                    {...updateMethods.register("admNm")}
                    error={!!updateMethods.formState.errors.admNm}
                    helperText={updateMethods.formState.errors.admNm?.message}
                  />
                </FormControl>
              </Grid>
              <Grid size={{ md: 6 }} display={{ xs: "none", md: "block" }} />
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <FormLabel required>이메일</FormLabel>
                  <TextField
                    fullWidth
                    type="email"
                    variant="outlined"
                    color="primary"
                    size="small"
                    placeholder="이메일을 입력해주세요."
                    {...updateMethods.register("email")}
                    error={!!updateMethods.formState.errors.email}
                    helperText={updateMethods.formState.errors.email?.message}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <Divider />
          <Box>
            <Typography variant={"h6"} sx={{ mb: 2 }}>
              권한 정보
            </Typography>
            <Grid container columnSpacing={2} rowGap={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <FormLabel required>관리자 권한</FormLabel>
                  <Controller
                    name="admAuth"
                    control={updateMethods.control}
                    render={({ field }) => (
                      <Admins.AuthSelect
                        {...field}
                        error={!!updateMethods.formState.errors.admAuth}
                      />
                    )}
                  />
                  <FormHelperText
                    error={!!updateMethods.formState.errors.admAuth}
                  >
                    {updateMethods.formState.errors.admAuth?.message}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
          <Divider />
          <Box>
            <Controller
              name="useYn"
              control={updateMethods.control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value === Yn.Y}
                      onChange={(e) =>
                        field.onChange(e.target.checked ? Yn.Y : Yn.N)
                      }
                    />
                  }
                  label="사용 여부"
                />
              )}
            />
          </Box>
          <Box
            display={"flex"}
            alignContent={"center"}
            width={"100%"}
            justifyContent={"center"}
            gap={2}
            mt={10}
          >
            <SaveButton onClick={() => handleAdmUpdate()}>
              매니저 정보 변경
            </SaveButton>
          </Box>
        </Stack>
      </FormProvider>
      <Divider />
      <FormProvider {...pwdUpdateMethods}>
        <Stack direction={"column"} gap={4}>
          <Box>
            <Typography variant={"h5"} sx={{ mb: 2 }}>
              비밀번호 변경
            </Typography>
            <Grid container>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <FormLabel required>비밀번호</FormLabel>
                  <TextField
                    fullWidth
                    type="password"
                    variant="outlined"
                    color="primary"
                    size="small"
                    placeholder="비밀번호를 입력해주세요."
                    {...pwdUpdateMethods.register("newPassword")}
                    error={
                      !!pwdUpdateMethods.formState.errors.newPassword ||
                      !!pwdUpdateMethods.formState.errors.newPasswordConfirm
                    }
                    sx={{ mb: 1 }}
                  />
                  <TextField
                    fullWidth
                    type="password"
                    variant="outlined"
                    color="primary"
                    size="small"
                    placeholder="비밀번호 확인을 입력해주세요."
                    {...pwdUpdateMethods.register("newPasswordConfirm")}
                    error={
                      !!pwdUpdateMethods.formState.errors.newPassword ||
                      !!pwdUpdateMethods.formState.errors.newPasswordConfirm
                    }
                  />
                  <FormHelperText
                    error={
                      !!pwdUpdateMethods.formState.errors.newPassword ||
                      !!pwdUpdateMethods.formState.errors.newPasswordConfirm
                    }
                  >
                    {pwdUpdateMethods.formState.errors.newPassword?.message ||
                      pwdUpdateMethods.formState.errors.newPasswordConfirm
                        ?.message}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Grid>
            <Box
              display={"flex"}
              alignContent={"center"}
              width={"100%"}
              justifyContent={"center"}
              mt={10}
            >
              <SaveButton onClick={() => handleAdmPwdUpdate()}>
                비밀번호 변경
              </SaveButton>
            </Box>
          </Box>
        </Stack>
      </FormProvider>
    </Box>
  );
};
